import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { isCustomerViewBootProfile } from "../utils/adminCustomerViewBoot";
import { RealtimeStreamClient, type RealtimeEventHandler } from "../services/realtimeStreamClient";
import type { RealtimeBusinessEventType, RealtimeConnectionState } from "../types/realtime";

/**
 * Session-scoped owner of the gateway SSE stream (PWA-2689 Section 3.4.4).
 *
 * One client per tab for the whole signed-in session: starts once auth has
 * resolved, stops on logout (the auth session transition also aborts the
 * fetch itself), restarts when the actor or the active account changes.
 * Domain code subscribes with `useRealtimeEvent` / `useRealtimeReady`; this
 * provider knows nothing about notifications.
 */

export interface RealtimeContextValue {
  enabled: boolean;
  connection: RealtimeConnectionState;
  subscribe: RealtimeStreamClient["subscribe"];
  onReady: RealtimeStreamClient["onReady"];
}

const noop = () => () => undefined;

const INERT_VALUE: RealtimeContextValue = {
  enabled: false,
  connection: "idle",
  subscribe: noop,
  onReady: noop,
};

const RealtimeContext = createContext<RealtimeContextValue>(INERT_VALUE);

interface RealtimeProviderProps {
  children: React.ReactNode;
  /** Test seam; production uses a real client. */
  createClient?: () => RealtimeStreamClient;
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children, createClient }) => {
  const { isAuthenticated, isLoading, actorUserId, effectiveUserId } = useAuth();
  const enabled = isAuthenticated && !isLoading && !isCustomerViewBootProfile();

  const clientRef = useRef<RealtimeStreamClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = createClient ? createClient() : new RealtimeStreamClient();
  }
  const client = clientRef.current;
  const [connection, setConnection] = useState<RealtimeConnectionState>(client.state);

  useEffect(() => client.onStateChange(setConnection), [client]);

  // Start/stop follows the session; restart follows identity changes while enabled.
  useEffect(() => {
    if (!enabled) {
      client.stop();
      return undefined;
    }
    client.restart();
    return () => client.stop();
  }, [client, enabled, actorUserId, effectiveUserId]);

  // Stable identities so subscriber effects do not re-run on every connection change.
  const subscribe = useCallback<RealtimeStreamClient["subscribe"]>(
    (type, handler) => client.subscribe(type, handler),
    [client]
  );
  const onReady = useCallback<RealtimeStreamClient["onReady"]>(handler => client.onReady(handler), [client]);

  const value = useMemo<RealtimeContextValue>(
    () => (enabled ? { enabled: true, connection, subscribe, onReady } : { ...INERT_VALUE, connection: "idle" }),
    [connection, enabled, onReady, subscribe]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export function useRealtimeConnection(): { enabled: boolean; connection: RealtimeConnectionState } {
  const { enabled, connection } = useContext(RealtimeContext);
  return { enabled, connection };
}

/** Subscribes for the component's lifetime; the latest handler is always the one invoked. */
export function useRealtimeEvent<T extends RealtimeBusinessEventType>(type: T, handler: RealtimeEventHandler<T>): void {
  const { enabled, subscribe } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribe(type, (event => handlerRef.current(event)) as RealtimeEventHandler<T>);
  }, [enabled, subscribe, type]);
}

/** Runs on every stream.ready (first connect and each reconnect) so a domain can refetch authoritative state. */
export function useRealtimeReady(handler: () => void): void {
  const { enabled, onReady } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!enabled) return undefined;
    return onReady(() => handlerRef.current());
  }, [enabled, onReady]);
}

export default RealtimeContext;
